import { useCallback, useEffect, useState } from "react";
import { BowlFood, Clock, Trash, Storefront, ShoppingCart, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Fridge() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState("");
  const [fridge, setFridge] = useState({ name: "", building: "", floor: "", where: "", shops: ["Jewel-Osco"], pays: "employer", fulfilment: "either", people: 20 });
  const [item, setItem] = useState({ name: "", unit: "tub", keeps_days: 2, cost: 0, price: 0, free_to_staff: true, shop: "" });
  const [sched, setSched] = useState({ days: ["Monday", "Wednesday", "Friday"], at_time: "08:00", lines: {} });
  const [take, setTake] = useState({});

  const load = useCallback(() => {
    api.get(`/fridge${pick ? `?fridge_id=${pick}` : ""}`)
      .then(({ data: d }) => setData(d)).catch(oops);
  }, [pick]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const stockRun = async (run) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment";
    input.onchange = async () => {
      const file = input.files?.[0];
      let photo_id = "";
      if (file) {
        const form = new FormData();
        form.append("file", file);
        form.append("caption", `${run.fridge_name} stocked`);
        try { const { data: up } = await api.post("/images", form); photo_id = up.id; }
        catch (e) { oops(e); return; }
      }
      act(() => api.post(`/fridge/runs/${run.id}/stocked`, { photo_id }), "In the fridge");
    };
    input.click();
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Opening the fridge…</p>;
  const current = data.fridges.find((f) => f.id === data.fridge_id);

  return (
    <div className="space-y-6" data-testid="fridge-page">
      <div>
        <p className="overline text-[#B3241B]">Workplace fridge</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <BowlFood size={30} weight="duotone" className="text-[#0B5C2E]" /> Fresh in the fridge
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="fridge-explainer">
          Cut fruit, salads, sandwiches and milk brought in from the shops on a standing
          schedule and left in the fridge for everyone. Help yourself and tick it off, so the
          next order is the right size and nothing sits there going off.
        </p>
      </div>

      {data.fridges.length > 0 && (
        <select data-testid="fridge-pick" value={data.fridge_id} aria-label="Fridge"
          onChange={(e) => setPick(e.target.value)}
          className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
          {data.fridges.map((f) => (
            <option key={f.id} value={f.id}>
              {`${f.name}${f.building ? ` · ${f.building}` : ""}${f.floor ? ` · ${f.floor}` : ""}`}
            </option>
          ))}
        </select>
      )}

      {data.past_use_by.length > 0 && (
        <div className="border-l-2 border-[#B3241B] bg-[#FDF2F2] px-4 py-3" data-testid="fridge-over">
          <p className="overline text-[#B3241B]">Past its use-by — bin it</p>
          <ul className="mt-1 space-y-1 text-sm text-slate-700">
            {data.past_use_by.map((s) => (
              <li key={s.id} data-testid={`over-${s.id}`}>
                {s.left} {s.unit} {s.name} · use-by {s.use_by}
                <Button className="ml-2" size="sm" variant="outline" data-testid={`bin-${s.id}`}
                  disabled={busy}
                  onClick={() => act(() => api.post(`/fridge/stock/${s.id}/waste`, { reason: "past its use-by" }), "Binned and logged")}>
                  <Trash size={13} className="mr-1" /> Bin it
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.eat_today.length > 0 && (
        <div className="border-l-2 border-[#C77A0A] bg-[#FFF8EC] px-4 py-3" data-testid="fridge-eat-today">
          <p className="overline text-[#C77A0A]">
            <Clock size={13} className="mr-1 inline align-[-2px]" /> Eat me today
          </p>
          <p className="mt-1 text-sm text-slate-700">
            {data.eat_today.map((s) => `${s.left} ${s.unit} ${s.name}`).join(" · ")}
          </p>
        </div>
      )}

      <div data-testid="fridge-stock">
        <p className="overline text-slate-500">What is in there now</p>
        {data.stock.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">The fridge is empty — raise a run.</p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {data.stock.map((s) => (
              <div key={s.id} className="border border-border bg-white p-3" data-testid={`stock-${s.id}`}>
                <p className="text-sm font-semibold text-[#0F172A]">{s.name}</p>
                <p className="text-xs text-slate-500">
                  {s.left} of {s.quantity} {s.unit} left · use-by {s.use_by}
                  {s.days_left != null && ` · ${s.days_left} day(s)`}
                </p>
                <p className="mt-1 text-xs font-semibold text-[#0B5C2E]">
                  {s.free_to_staff || Number(s.price || 0) <= 0
                    ? "Free to staff"
                    : `${s.symbol}${Number(s.price).toFixed(2)} a ${s.unit}`}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input data-testid={`take-qty-${s.id}`} type="number" min={0} step="0.5" className="w-16"
                    value={take[s.id] ?? 1}
                    onChange={(e) => setTake({ ...take, [s.id]: e.target.value })} />
                  <Button size="sm" data-testid={`take-${s.id}`} disabled={busy}
                    onClick={() => act(async () => {
                      const { data: out } = await api.post(`/fridge/stock/${s.id}/take`, {
                        quantity: Number(take[s.id] ?? 1) || 1 });
                      if (out.charge) {
                        const { data: p } = await api.post("/payments/fridge/checkout", {
                          charge_id: out.charge.id, origin_url: window.location.origin });
                        if (p.url) window.location.href = p.url;
                      }
                    }, "Enjoy it")}>
                    I took some
                  </Button>
                  {data.can_manage && (
                    <Button size="sm" variant="outline" data-testid={`waste-${s.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/fridge/stock/${s.id}/waste`, { reason: "not eaten" }), "Logged")}>
                      Bin
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(data.my_runs || []).length > 0 && (
        <div data-testid="fridge-my-runs">
          <p className="overline text-slate-500">Your shopping run</p>
          <div className="mt-2 space-y-2">
            {data.my_runs.map((r) => (
              <div key={r.id} className="border border-border bg-white p-3 text-sm" data-testid={`myrun-${r.id}`}>
                <p className="font-semibold text-[#0F172A]">
                  {r.fridge_name} · {r.day} · {(r.shops || []).join(", ")} ·
                  {" "}{r.symbol}{Number(r.cost || 0).toFixed(2)}
                </p>
                <ul className="mt-1 text-xs text-slate-600">
                  {(r.lines || []).map((l, i) => (
                    <li key={i}>{l.quantity} {l.unit} {l.name} — {l.shop}</li>
                  ))}
                </ul>
                <Button className="mt-2" size="sm" data-testid={`stocked-${r.id}`} onClick={() => stockRun(r)}>
                  <Camera size={14} className="mr-1.5" /> It is in the fridge
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.my_charges || []).filter((c) => c.payment_status === "unpaid").length > 0 && (
        <div data-testid="fridge-charges">
          <p className="overline text-slate-500">What you owe</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {data.my_charges.filter((c) => c.payment_status === "unpaid").map((c) => (
              <li key={c.id} data-testid={`charge-${c.id}`}>
                {c.quantity} × {c.name} · {c.symbol}{Number(c.total || 0).toFixed(2)}
                <Button className="ml-2" size="sm" data-testid={`charge-pay-${c.id}`} disabled={busy}
                  onClick={async () => {
                    try {
                      const { data: p } = await api.post("/payments/fridge/checkout", {
                        charge_id: c.id, origin_url: window.location.origin });
                      if (p.url) window.location.href = p.url;
                    } catch (e) { oops(e); }
                  }}>
                  Pay it
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.can_manage && (
        <>
          <div className="border border-border bg-white p-4" data-testid="fridge-add-box">
            <p className="overline text-slate-500">Register a fridge</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <Input data-testid="fridge-name" placeholder="Fridge name — '3rd floor kitchen'"
                value={fridge.name} onChange={(e) => setFridge({ ...fridge, name: e.target.value })} />
              <Input data-testid="fridge-building" placeholder="Building"
                value={fridge.building} onChange={(e) => setFridge({ ...fridge, building: e.target.value })} />
              <Input data-testid="fridge-floor" placeholder="Floor"
                value={fridge.floor} onChange={(e) => setFridge({ ...fridge, floor: e.target.value })} />
              <Input data-testid="fridge-where" placeholder="Town or postcode"
                value={fridge.where} onChange={(e) => setFridge({ ...fridge, where: e.target.value })} />
              <select data-testid="fridge-pays" value={fridge.pays} aria-label="Who pays"
                onChange={(e) => setFridge({ ...fridge, pays: e.target.value })}
                className="tap-safe border border-border bg-white px-3 py-2 text-sm">
                {(data.pays || []).map((p) => <option key={p} value={p}>{`${p} pays`}</option>)}
              </select>
              <select data-testid="fridge-fulfilment" value={fridge.fulfilment} aria-label="Fulfilment"
                onChange={(e) => setFridge({ ...fridge, fulfilment: e.target.value })}
                className="tap-safe border border-border bg-white px-3 py-2 text-sm">
                {(data.fulfilment || []).map((f) => <option key={f} value={f}>{String(f)}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                People it feeds
                <Input data-testid="fridge-people" type="number" min={1} className="w-20"
                  value={fridge.people} onChange={(e) => setFridge({ ...fridge, people: e.target.value })} />
              </label>
            </div>
            <p className="overline mt-3 text-slate-500">
              <Storefront size={13} className="mr-1 inline align-[-2px]" /> Shops it buys from
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(data.shops || []).map((s) => (
                <button key={s} type="button" data-testid={`shop-${s.replace(/[^a-z0-9]/gi, "-")}`}
                  onClick={() => setFridge({
                    ...fridge,
                    shops: fridge.shops.includes(s)
                      ? fridge.shops.filter((x) => x !== s) : [...fridge.shops, s],
                  })}
                  className={`tap-safe border px-2.5 py-2 text-xs transition-colors duration-150 ${
                    fridge.shops.includes(s)
                      ? "border-[#0B5C2E] bg-[#0B5C2E] text-white"
                      : "border-border bg-white text-slate-600 hover:border-[#0B5C2E]"}`}>
                  {s}
                </button>
              ))}
            </div>
            <Button className="mt-3" size="sm" data-testid="fridge-save"
              disabled={busy || fridge.name.trim().length < 2}
              onClick={() => act(async () => {
                await api.post("/fridge/fridges", { ...fridge, people: Number(fridge.people) || 20 });
                setFridge({ ...fridge, name: "", building: "", floor: "" });
              }, "Fridge registered with a starter list")}>
              Register it
            </Button>
          </div>

          {current && (
            <div className="border border-border bg-white p-4" data-testid="fridge-schedule-box">
              <p className="overline text-slate-500">Standing order for {current.name}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(data.days || []).map((d) => (
                  <button key={d} type="button" data-testid={`sched-day-${d}`}
                    onClick={() => setSched({
                      ...sched,
                      days: sched.days.includes(d)
                        ? sched.days.filter((x) => x !== d) : [...sched.days, d],
                    })}
                    className={`tap-safe border px-2.5 py-2 text-xs transition-colors duration-150 ${
                      sched.days.includes(d)
                        ? "border-[#002FA7] bg-[#002FA7] text-white"
                        : "border-border bg-white text-slate-600 hover:border-[#002FA7]"}`}>
                    {d.slice(0, 3)}
                  </button>
                ))}
                <Input data-testid="sched-time" className="w-24" placeholder="08:00"
                  value={sched.at_time} onChange={(e) => setSched({ ...sched, at_time: e.target.value })} />
              </div>
              <div className="mt-3 grid gap-1 sm:grid-cols-2">
                {(data.catalogue || []).map((c) => (
                  <label key={c.id} className="tap-safe flex items-center justify-between gap-2 border border-border px-2 py-2 text-xs text-slate-700">
                    <span>{c.name} <span className="text-slate-400">({c.unit}, keeps {c.keeps_days}d)</span></span>
                    <Input data-testid={`sched-qty-${c.id}`} type="number" min={0} className="w-16"
                      value={sched.lines[c.id] ?? ""}
                      onChange={(e) => setSched({ ...sched, lines: { ...sched.lines, [c.id]: e.target.value } })} />
                  </label>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" data-testid="sched-save" disabled={busy}
                  onClick={() => act(() => api.post("/fridge/schedules", {
                    fridge_id: current.id, days: sched.days, at_time: sched.at_time,
                    lines: Object.entries(sched.lines)
                      .filter(([, q]) => Number(q) > 0)
                      .map(([item_id, q]) => ({ item_id, quantity: Number(q) })),
                  }), "Standing order set")}>
                  Set the standing order
                </Button>
                <Button size="sm" variant="outline" data-testid="sched-topup" disabled={busy}
                  onClick={() => act(() => api.post("/fridge/runs", {
                    fridge_id: current.id,
                    lines: Object.entries(sched.lines)
                      .filter(([, q]) => Number(q) > 0)
                      .map(([item_id, q]) => ({ item_id, quantity: Number(q) })),
                  }), "Top-up run raised")}>
                  <ShoppingCart size={14} className="mr-1.5" /> Raise a one-off top-up
                </Button>
                <Button size="sm" variant="outline" data-testid="sched-generate" disabled={busy}
                  onClick={() => act(() => api.post("/fridge/runs/generate"), "Today's runs raised")}>
                  Raise today's runs now
                </Button>
              </div>
            </div>
          )}

          <div className="border border-border bg-white p-4" data-testid="fridge-item-box">
            <p className="overline text-slate-500">Add something to the list</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <Input data-testid="item-name" placeholder="Name — 'Fresh cut mango'"
                value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} />
              <select data-testid="item-unit" value={item.unit} aria-label="Unit"
                onChange={(e) => setItem({ ...item, unit: e.target.value })}
                className="tap-safe border border-border bg-white px-3 py-2 text-sm">
                {(data.units || []).map((u) => <option key={u} value={u}>{String(u)}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                Keeps (days)
                <Input data-testid="item-keeps" type="number" min={0} className="w-20"
                  value={item.keeps_days} onChange={(e) => setItem({ ...item, keeps_days: e.target.value })} />
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                Shop cost
                <Input data-testid="item-cost" type="number" min={0} step="0.01" className="w-24"
                  value={item.cost} onChange={(e) => setItem({ ...item, cost: e.target.value })} />
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid="item-free" checked={item.free_to_staff}
                  onChange={(e) => setItem({ ...item, free_to_staff: e.target.checked })} />
                Free to staff
              </label>
              {!item.free_to_staff && (
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  Staff pay
                  <Input data-testid="item-price" type="number" min={0} step="0.01" className="w-24"
                    value={item.price} onChange={(e) => setItem({ ...item, price: e.target.value })} />
                </label>
              )}
            </div>
            <Button className="mt-2" size="sm" data-testid="item-save"
              disabled={busy || !current || item.name.trim().length < 2}
              onClick={() => act(async () => {
                await api.post("/fridge/items", {
                  fridge_id: current.id, name: item.name, unit: item.unit,
                  keeps_days: Number(item.keeps_days) || 0, cost: Number(item.cost) || 0,
                  price: Number(item.price) || 0, free_to_staff: item.free_to_staff,
                });
                setItem({ ...item, name: "" });
              }, "On the list")}>
              Add it
            </Button>
          </div>
        </>
      )}

      {(data.runs || []).length > 0 && (
        <div data-testid="fridge-runs">
          <p className="overline text-slate-500">Runs</p>
          <div className="mt-2 space-y-2">
            {data.runs.map((r) => (
              <div key={r.id} className="border border-border bg-white p-3 text-sm" data-testid={`run-${r.id}`}>
                <p className="font-semibold text-[#0F172A]">
                  {r.day} · {r.standing ? "standing order" : "top-up"} · {(r.shops || []).join(", ")} ·
                  {" "}{r.symbol}{Number(r.cost || 0).toFixed(2)} · {r.status}
                  {r.runner_name ? ` · ${r.runner_name}` : ""}
                </p>
                {r.photo_url && (
                  <img src={`${BACKEND}${r.photo_url}`} alt="Stocked fridge" loading="lazy"
                    className="mt-2 h-28 w-full border border-border object-cover sm:w-44"
                    data-testid={`run-photo-${r.id}`} />
                )}
                {r.status !== "stocked" && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {!r.runner_id && (
                      <Button size="sm" variant="outline" data-testid={`run-take-${r.id}`} disabled={busy}
                        onClick={() => act(() => api.post(`/fridge/runs/${r.id}/assign`), "It is yours")}>
                        I will do this run
                      </Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`run-stocked-${r.id}`}
                      onClick={() => stockRun(r)}>
                      It is in the fridge
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.waste || []).length > 0 && (
        <div data-testid="fridge-waste">
          <p className="overline text-slate-500">Waste log</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-600">
            {data.waste.map((w) => (
              <li key={w.id} data-testid={`waste-row-${w.id}`}>
                {w.created_at?.slice(0, 10)} · {w.quantity} {w.unit} {w.name} · {w.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
